<!--
  SuperAdmin Plans & Pricing.

  Sections (all live):
    1. GlobalDefaultMargin -- SA-global default base margin editor.
    2. PlatformDefaultTimezone -- platform-wide default timezone.
    3. PlatformFeeRate -- the ThreadFlow platform fee on the cart total.
    4. Platform plan catalog -- ONE expanding/collapsing card per plan
       (PlanCard.vue), replacing the old split plans-table + separate
       per-tier decoration-grid tables (Jake feedback 2026-08-04, card
       redesign; authority docs/planning/2026-08-04-sa-plans-pricing-card-
       redesign.md). This PARENT owns all API calls + the single-open
       accordion state; each card is a controlled/presentational component
       that only EMITS intents (mirrors the category editor,
       StoreCategories/index.vue + CategoryCard.vue).
    5. Supplier availability -- the tier x supplier matrix (which suppliers
       each plan includes) plus the "not in any plan" omission alarm. The
       per-plan toggles live INSIDE the cards; only the cross-tier alarm is
       page-level.
    5a. White-label branding -- one per-plan switch, also inside the card
       (Jake 2026-09-18/19: a feature of the subscription tier system,
       default off). Saves instantly; the store's own logo is a separate,
       store-level field.
    6. Distributor plan assignment -- ONE section answering "which plan is
       this distributor on", with two entry paths: move a single distributor
       by id, or pick a plan version, see who is still grandfathered onto it,
       and repoint a selection (which emails a terms-change notice).

  Sections 5 and 6 MOVED HERE 2026-08-28 from TierVersions.vue
  (/admin/tier-versions), which is deleted. That route had no navigation
  entry anywhere -- not in SuperAdminLayout's sidebar, not in any menu -- so
  it was reachable only by typing the URL, and the supplier section Jake
  asked for could not be found. The backend endpoints are unchanged; only
  the surface moved. Jake, 2026-08-28: "I want the UI gone. The
  grandfathering and reassignment logic is okay to keep but it needs to be
  wired into plans-versions instead."

  Card interactions the parent persists:
    - Inline name / monthly-price edits (debounce name; price on blur) ->
      PATCH /plans/:id.
    - Public/Hidden toggle -> instant PATCH is_public.
    - Active/Disabled toggle -> re-activating PATCHes is_active=true;
      disabling with subscribers opens the reassign modal (target picker)
      that POSTs /plans/:id/disable {target_plan_id}; disabling with zero
      subscribers POSTs /plans/:id/disable with no target.
    - Delete -> confirm dialog, then POST /plans/:id/archive (control is
      disabled when the plan has subscribers).
    - New Plan (page-level) -> a small create form (name + monthly price),
      POST /plans; the created plan is Hidden + carries cloned Tier A grids.

  ASCII-only.
-->
<template>
    <div data-testid="sa-plans-pricing">
        <h1 class="sa-page-title">Plans &amp; Pricing</h1>

        <!--
          Platform-wide settings: 2x2 grid (two rows, two columns), Time
          Zone first (Jake feedback 2026-08-04). Collapses to one column on
          narrow viewports.
        -->
        <div class="sa-settings-grid" data-testid="sa-settings-grid">
            <PlatformDefaultTimezone />

            <PlatformFeeRate @rate-change="onGlobalFeeRateChange" />

            <GlobalDefaultMargin />

            <!--
              TFBlankCostMargin is HIDDEN, not deleted (Jake 2026-08-26): the
              TF blank-cost margin is 0 across the board, so the editor is off
              the surface -- but the component, its GET/PATCH endpoint, its
              platform-settings row and its unit spec all remain. To UN-HIDE:
              restore `<TFBlankCostMargin />` here, plus its import and its
              `components:` entry below. That is the whole change.
            -->
        </div>

        <!-- Plan catalog: cards + New Plan. -->
        <section class="sa-plans" data-testid="sa-plans-section">
            <div class="sa-plans__head">
                <h2 class="sa-section-title">Subscription plans</h2>
                <button
                    type="button"
                    class="sa-btn sa-btn--primary"
                    data-testid="sa-new-plan-btn"
                    @click="openCreate"
                >
                    + New plan
                </button>
            </div>

            <p
                v-if="loading"
                class="sa-state"
                data-testid="sa-plans-loading"
            >
                Loading plans...
            </p>

            <p
                v-else-if="errorMsg"
                class="sa-state sa-state--error"
                data-testid="sa-plans-error"
            >
                {{ errorMsg }}
            </p>

            <p
                v-else-if="plans.length === 0"
                class="sa-state"
                data-testid="sa-plans-empty"
            >
                No plans configured yet.
            </p>

            <p
                v-if="reorderError"
                class="sa-state sa-state--error"
                role="alert"
                data-testid="sa-plans-reorder-error"
            >
                {{ reorderError }}
            </p>

            <div v-if="showPlanCards" ref="planCards" data-testid="sa-plan-cards">
                <PlanCard
                    v-for="(p, idx) in plans"
                    :key="p.id"
                    :plan="p"
                    :can-move-up="idx > 0 && !reorderSaving"
                    :can-move-down="idx < plans.length - 1 && !reorderSaving"
                    @move-up="movePlan(idx, idx - 1)"
                    @move-down="movePlan(idx, idx + 1)"
                    :grid-tier="gridTierForPlan(p)"
                    :app-fee-basis-points="appFeeForPlan(p)"
                    :expanded="openPlanId === p.id"
                    :global-platform-fee-rate="globalPlatformFeeRate"
                    :save-error="planSaveErrors[p.id] || ''"
                    :supplier-tier="supplierTierForPlan(p)"
                    :suppliers-loading="supplierLoading"
                    :suppliers-saving="supplierSavingTier === tierKeyForPlan(p)"
                    :suppliers-saved="supplierSavedTier === tierKeyForPlan(p)"
                    :suppliers-dirty="dirtyTiers.includes(tierKeyForPlan(p))"
                    :suppliers-error="supplierError"
                    :white-label-enabled="whiteLabelForPlan(p)"
                    :white-label-loading="whiteLabelLoading"
                    :white-label-saving="whiteLabelSavingTier === tierKeyForPlan(p)"
                    :white-label-saved="whiteLabelSavedTier === tierKeyForPlan(p)"
                    :white-label-error="whiteLabelError"
                    :art-services="artServicesForPlan(p)"
                    :art-services-loading="artServicesLoading"
                    :art-services-saving="artServicesSavingTier === tierKeyForPlan(p)"
                    :art-services-saved="artServicesSavedTier === tierKeyForPlan(p)"
                    :art-services-error="artServicesErrorTier === tierKeyForPlan(p) || artServicesErrorTier === '*' ? artServicesError : ''"
                    :tier-key="tierKeyForPlan(p)"
                    @toggle-expand="onToggleExpand"
                    @toggle-active="onToggleActive"
                    @toggle-public="onTogglePublic"
                    @set-default-signup="onSetDefaultSignup"
                    @delete-plan="onRequestDelete"
                    @update-name="onUpdateName"
                    @update-trial-days="onUpdateTrialDays"
                    @update-description="onUpdateDescription"
                    @update-presentation="onUpdatePresentation"
                    @update-price="onUpdatePrice"
                    @update-store-class-price="onUpdateStoreClassPrice"
                    @update-included-stores="onUpdateIncludedStores"
                    @update-fee-override="onUpdateFeeOverride"
                    @update-shipping-surcharge="onUpdateShippingSurcharge"
                    @toggle-supplier="onSupplierToggle"
                    @save-suppliers="saveTierSuppliers"
                    @toggle-white-label="onWhiteLabelToggle"
                    @save-art-services="onSaveArtServices"
                />
            </div>
        </section>

        <!--
          THE OMISSION ALARM -- page-level, deliberately NOT in a card.

          "This supplier is in NO plan" is a fact about the SET of plans, so
          no single card can host it: the supplier it names is by definition
          absent from every card, and burying it behind an expand would make
          the one silent failure silent again. It is the safety net for the
          whole tier-carries-the-list design (see the migration docstring in
          tier_suppliers_001_supplier_availability.py) -- the cost Jake
          accepted is that a new supplier means touching every plan, and
          forgetting one omits it SILENTLY, looking like an empty catalog
          rather than a gating decision. This warning is how that cost is
          paid down.

          The per-plan toggles moved INTO the cards 2026-08-29 (Jake: "I
          expected supplier availability to be inside the expandable cards
          for each subscription plan"). Only this cross-tier warning stayed.
        -->
        <p
            v-if="!supplierLoading && unconfiguredSuppliers.length"
            class="sa-warning"
            data-testid="sa-tier-suppliers-unconfigured"
        >
            <strong>Not in any plan:</strong>
            {{ unconfiguredSupplierLabel }}. No distributor can sell from
            {{ unconfiguredSuppliers.length === 1 ? "it" : "them" }}
            until
            {{ unconfiguredSuppliers.length === 1 ? "it is" : "they are" }}
            enabled in a plan above.
        </p>

        <!--
          Distributor plan assignment -- ONE section, two entry paths.

          docs/design-philosophy.md #2 (one mechanism per concept): "which
          plan is this distributor on?" is a single product question, so it
          gets a single section. Before 2026-08-28 the answer was split
          across this page (reassign ONE distributor by id) and
          /admin/tier-versions (repoint MANY off a version's grandfathering
          table). Shipping both as sibling sections on one page would have
          left two controls that mean the same thing and disagree about
          whether an email goes out.

          The two paths are kept because they answer DIFFERENT questions the
          admin arrives with -- "move this one distributor" (you know who)
          versus "move everyone still on the old terms" (you know the
          version, not the names). Only the bulk path emails, because only it
          changes terms someone was grandfathered onto.
        -->
        <section
            class="sa-reassign"
            data-testid="sa-reassign-plan-section"
        >
            <h2 class="sa-section-title">Distributor plan assignment</h2>

            <h3 class="sa-subsection-title">Move one distributor</h3>
            <p class="sa-section-hint">
                Move a distributor onto a different plan -- including the
                hidden Tier B (bigger-client / special arrangement). The
                distributor is pinned to the target plan's current version.
            </p>
            <form
                class="sa-reassign__form"
                data-testid="sa-reassign-form"
                @submit.prevent="submitReassign"
            >
                <div class="sa-field sa-field--picker">
                    <DistributorPicker
                        v-model="reassignForm.distributor"
                        label="Distributor"
                    />
                </div>
                <div class="sa-field">
                    <label class="sa-label" for="reassign-plan">
                        Target plan
                    </label>
                    <select
                        id="reassign-plan"
                        v-model="reassignForm.planId"
                        class="sa-input"
                        data-testid="sa-reassign-target-plan"
                    >
                        <option :value="null" disabled>Select a plan...</option>
                        <option
                            v-for="p in plans"
                            :key="p.id"
                            :value="p.id"
                        >
                            {{ p.name }}{{ p.is_public === false ? " (hidden)" : "" }}
                        </option>
                    </select>
                </div>
                <button
                    class="sa-btn sa-btn--primary"
                    type="submit"
                    data-testid="sa-reassign-submit"
                    :disabled="
                        reassigning ||
                        !reassignForm.distributor ||
                        !reassignForm.planId ||
                        reassignAlreadyOnTarget
                    "
                >
                    {{ reassigning ? "Reassigning..." : "Reassign" }}
                </button>
            </form>
            <p
                v-if="reassignAlreadyOnTarget"
                class="sa-reassign__note"
                data-testid="sa-reassign-already-on-plan"
            >
                {{ reassignForm.distributor.name }} is already on
                {{ reassignTargetPlan.name }}. Choose a different plan to move
                them.
            </p>
            <p
                v-if="reassignResult"
                class="sa-reassign__result"
                data-testid="sa-reassign-result"
            >
                {{ reassignResult }}
            </p>
            <p
                v-if="reassignError"
                class="sa-save-error"
                data-testid="sa-reassign-error"
            >
                {{ reassignError }}
            </p>

            <!--
              Grandfathering: who is still pinned to an older version of a
              plan's terms, and moving a batch of them forward.

              A rate change is a NEW VERSION -- existing distributors keep
              the terms they signed up under until someone moves them here.
              That is the only place in the product where those pinned
              distributors are visible at all.
            -->
            <h3 class="sa-subsection-title">Move everyone on a version</h3>
            <p class="sa-section-hint">
                A rate change is a new version -- distributors keep the terms
                they signed up under until repointed. Pick a version to see
                who is still on it. Each distributor moved is emailed a
                terms-change notice.
            </p>

            <p
                v-if="versionError"
                class="sa-save-error"
                data-testid="sa-grandfather-error"
            >
                {{ versionError }}
            </p>

            <div class="sa-field">
                <label class="sa-label" for="grandfather-version">
                    Show distributors on version
                </label>
                <select
                    id="grandfather-version"
                    v-model.number="browseVersionId"
                    class="sa-input"
                    data-testid="sa-grandfather-version-select"
                    @change="onBrowseVersion"
                >
                    <option :value="null" disabled>
                        Select a version...
                    </option>
                    <option
                        v-for="v in allVersions"
                        :key="v.id"
                        :value="v.id"
                    >
                        {{ v.tier_key }} / {{ v.version_label }}
                    </option>
                </select>
            </div>

            <template v-if="browseVersionId">
                <p
                    v-if="distLoading"
                    class="sa-state"
                    data-testid="sa-dist-loading"
                >
                    Loading distributors...
                </p>

                <template v-else>
                    <p
                        v-if="distributors.length === 0"
                        class="sa-state"
                        data-testid="sa-no-distributors"
                    >
                        No distributors are pinned to this version.
                    </p>

                    <table
                        v-else
                        class="sa-matrix"
                        data-testid="sa-distributors-table"
                    >
                        <thead>
                            <tr>
                                <th class="sa-checkbox-col"></th>
                                <th>Distributor</th>
                                <th>Owner email</th>
                                <th>Repointed</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="d in distributors"
                                :key="d.distributor_id"
                                data-testid="sa-distributor-row"
                            >
                                <td>
                                    <input
                                        v-model="selectedDistributorIds"
                                        type="checkbox"
                                        :value="d.distributor_id"
                                        data-testid="sa-distributor-checkbox"
                                    />
                                </td>
                                <td>{{ d.name }}</td>
                                <td>{{ d.owner_email || "(no owner)" }}</td>
                                <td>
                                    <span v-if="d.repointed_at">
                                        {{ formatDate(d.repointed_at) }}
                                    </span>
                                    <span v-else class="sa-muted">
                                        pinned at signup
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="sa-repoint-row">
                        <label class="sa-label" for="repoint-target">
                            Move selected to
                        </label>
                        <select
                            id="repoint-target"
                            v-model.number="repointTargetId"
                            class="sa-input"
                            data-testid="sa-repoint-target-select"
                        >
                            <option :value="null" disabled>
                                Choose a version...
                            </option>
                            <option
                                v-for="v in allVersions"
                                :key="v.id"
                                :value="v.id"
                            >
                                {{ v.tier_key }} / {{ v.version_label }}
                            </option>
                        </select>

                        <button
                            type="button"
                            class="sa-btn sa-btn--primary"
                            :disabled="!canRepoint"
                            data-testid="sa-repoint-btn"
                            @click="openRepointConfirm"
                        >
                            Repoint {{ selectedDistributorIds.length }}
                            selected
                        </button>
                    </div>

                    <!--
                      The confirm step is the guard on a bulk action that
                      also EMAILS every affected distributor. An accidental
                      repoint cannot be recalled once the mail is out, so
                      the count and the target are stated before it fires.
                    -->
                    <div
                        v-if="repointConfirmOpen"
                        class="sa-confirm"
                        data-testid="sa-repoint-confirm"
                    >
                        <p data-testid="sa-repoint-confirm-message">
                            {{ selectedDistributorIds.length }}
                            distributor(s) will be moved to
                            <strong>{{ repointTargetLabel }}</strong>
                            and emailed.
                        </p>
                        <div class="sa-modal__actions">
                            <button
                                type="button"
                                class="sa-btn sa-btn--ghost"
                                :disabled="repointing"
                                data-testid="sa-repoint-cancel-btn"
                                @click="repointConfirmOpen = false"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                class="sa-btn sa-btn--primary"
                                :disabled="repointing"
                                data-testid="sa-repoint-confirm-btn"
                                @click="doRepoint"
                            >
                                {{
                                    repointing
                                        ? "Repointing..."
                                        : "Confirm repoint"
                                }}
                            </button>
                        </div>
                    </div>

                    <p
                        v-if="repointResult"
                        class="sa-reassign__result"
                        data-testid="sa-repoint-result"
                    >
                        Repointed {{ repointResult.repointed_count }},
                        skipped {{ repointResult.skipped_count }},
                        failed {{ repointResult.failed_count }}.
                    </p>
                </template>
            </template>
        </section>

        <!-- New-plan create modal -->
        <div
            v-if="creating"
            class="sa-modal-overlay"
            data-testid="sa-new-plan-modal"
            @click.self="cancelCreate"
        >
            <div class="sa-modal">
                <div class="sa-modal__head">
                    <h2 class="sa-modal__title">New plan</h2>
                    <button
                        class="sa-modal__close"
                        type="button"
                        @click="cancelCreate"
                    >
                        &times;
                    </button>
                </div>
                <form
                    class="sa-modal__form"
                    data-testid="sa-new-plan-form"
                    novalidate
                    @submit.prevent="submitCreate"
                >
                    <p class="sa-modal__notice">
                        A new plan starts Hidden (not on self-signup) and
                        clones Tier A's decoration grids. Publish it with the
                        Public toggle once its pricing is set.
                    </p>
                    <div class="sa-field">
                        <label class="sa-label" for="new-plan-name">
                            Display name
                        </label>
                        <input
                            id="new-plan-name"
                            v-model="createForm.name"
                            class="sa-input"
                            data-testid="sa-new-plan-name"
                            type="text"
                            placeholder="e.g. Enterprise"
                        />
                    </div>
                    <div class="sa-field" data-testid="sa-new-plan-price-field">
                        <label class="sa-label" for="new-plan-price">
                            Monthly price
                        </label>
                        <div class="sa-input-dollar">
                            <span class="sa-input-dollar__prefix">$</span>
                            <input
                                id="new-plan-price"
                                v-model="createForm.priceDollars"
                                class="sa-input sa-input--dollar"
                                data-testid="sa-new-plan-price"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                            />
                        </div>
                    </div>
                    <p
                        v-if="createError"
                        class="sa-save-error"
                        data-testid="sa-new-plan-error"
                    >
                        {{ createError }}
                    </p>
                    <div class="sa-modal__actions">
                        <button
                            class="sa-btn sa-btn--ghost"
                            type="button"
                            @click="cancelCreate"
                        >
                            Cancel
                        </button>
                        <button
                            class="sa-btn sa-btn--primary"
                            type="submit"
                            data-testid="sa-new-plan-submit"
                            :disabled="creatingBusy || !createForm.name.trim()"
                        >
                            {{ creatingBusy ? "Creating..." : "Create plan" }}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Disable-with-reassign modal -->
        <div
            v-if="disableTarget"
            class="sa-modal-overlay"
            data-testid="sa-disable-plan-modal"
            @click.self="cancelDisable"
        >
            <div class="sa-modal">
                <div class="sa-modal__head">
                    <h2 class="sa-modal__title">
                        Disable {{ disableTarget.name }}
                    </h2>
                    <button
                        class="sa-modal__close"
                        type="button"
                        @click="cancelDisable"
                    >
                        &times;
                    </button>
                </div>
                <form
                    class="sa-modal__form"
                    data-testid="sa-disable-plan-form"
                    @submit.prevent="submitDisable"
                >
                    <p class="sa-modal__notice">
                        This plan has
                        {{ disableTarget.subscriber_count }}
                        subscriber<template
                            v-if="disableTarget.subscriber_count !== 1"
                            >s</template
                        >. Pick an active plan to move
                        <template v-if="disableTarget.subscriber_count === 1"
                            >it</template
                        ><template v-else>them</template>
                        onto before disabling.
                    </p>
                    <div class="sa-field">
                        <label class="sa-label" for="disable-target">
                            Move subscribers to
                        </label>
                        <select
                            id="disable-target"
                            v-model="disableTargetPlanId"
                            class="sa-input"
                            data-testid="sa-disable-target-plan"
                        >
                            <option :value="null" disabled>
                                Select an active plan...
                            </option>
                            <option
                                v-for="p in disableTargetOptions"
                                :key="p.id"
                                :value="p.id"
                            >
                                {{ p.name }}{{ p.is_public === false ? " (hidden)" : "" }}
                            </option>
                        </select>
                    </div>
                    <p
                        v-if="disableError"
                        class="sa-save-error"
                        data-testid="sa-disable-error"
                    >
                        {{ disableError }}
                    </p>
                    <div class="sa-modal__actions">
                        <button
                            class="sa-btn sa-btn--ghost"
                            type="button"
                            @click="cancelDisable"
                        >
                            Cancel
                        </button>
                        <button
                            class="sa-btn sa-btn--primary"
                            type="submit"
                            data-testid="sa-disable-submit"
                            :disabled="disableBusy || !disableTargetPlanId"
                        >
                            {{ disableBusy ? "Disabling..." : "Reassign & disable" }}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Delete-confirm modal -->
        <div
            v-if="deleteTarget"
            class="sa-modal-overlay"
            data-testid="sa-delete-plan-modal"
            @click.self="cancelDelete"
        >
            <div class="sa-modal">
                <div class="sa-modal__head">
                    <h2 class="sa-modal__title">
                        Delete {{ deleteTarget.name }}?
                    </h2>
                    <button
                        class="sa-modal__close"
                        type="button"
                        @click="cancelDelete"
                    >
                        &times;
                    </button>
                </div>
                <div class="sa-modal__form">
                    <p class="sa-modal__notice">
                        This archives the plan -- it is hidden everywhere and
                        never resurfaced. Order and audit history keep their
                        references. This cannot be undone from here.
                    </p>
                    <p
                        v-if="deleteError"
                        class="sa-save-error"
                        data-testid="sa-delete-error"
                    >
                        {{ deleteError }}
                    </p>
                    <div class="sa-modal__actions">
                        <button
                            class="sa-btn sa-btn--ghost"
                            type="button"
                            @click="cancelDelete"
                        >
                            Cancel
                        </button>
                        <button
                            class="sa-btn sa-btn--danger"
                            type="button"
                            data-testid="sa-delete-confirm"
                            :disabled="deleteBusy"
                            @click="confirmDelete"
                        >
                            {{ deleteBusy ? "Deleting..." : "Delete plan" }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import GlobalDefaultMargin from "./GlobalDefaultMargin.vue";
import PlatformDefaultTimezone from "./PlatformDefaultTimezone.vue";
import PlatformFeeRate from "./PlatformFeeRate.vue";
import PlanCard from "./PlanCard.vue";
import Sortable from "sortablejs";
import DistributorPicker from "@/components/superadmin/DistributorPicker.vue";
import {
    listPlans,
    patchPlan,
    reassignDistributorPlan,
    getDecorationGrids,
    createPlan,
    archivePlan,
    disablePlan,
    reorderPlans,
    listPlanVersions,
    repointDistributors,
    distributorsOnVersion,
    getTierSuppliers,
    setTierSuppliers,
    getTierWhiteLabel,
    setTierWhiteLabel,
    getTierArtServices,
    setTierArtServices,
    setStoreClassPrice,
    setPlanIncludedStores,
} from "@/services/superadminPricingService";
import { dollarsToCents } from "@/utils/dollarsToCents";

// Debounce window for inline text edits (matches the category editor's
// autosave debounce). Name edits PATCH after the field settles.
const NAME_DEBOUNCE_MS = 350;

function serverMessage(e, fallback) {
    const msg = e?.response?.data?.message ?? e?.data?.message;
    return msg || fallback;
}

export default {
    name: "SuperAdminPlansPricing",
    components: {
        DistributorPicker,
        GlobalDefaultMargin,
        PlatformDefaultTimezone,
        PlatformFeeRate,
        PlanCard,
    },
    data() {
        return {
            plans: [],
            // Drag/move reorder: the last failed save, and an in-flight guard.
            reorderError: "",
            reorderSaving: false,
            // Global platform fee (fraction), reported by PlatformFeeRate
            // after its load/save; null until then. Shown by PlanCard.
            globalPlatformFeeRate: null,
            loading: true,
            errorMsg: "",

            // Single-open accordion -- the one source of truth for which card
            // is expanded (mirrors StoreCategories/index.vue openCategoryId).
            openPlanId: null,

            // Per-plan inline-edit save errors, keyed by plan id.
            planSaveErrors: {},

            // Decoration grids (fetched once; matched to plans by plan_id).
            gridTiers: [],

            // New-plan create modal.
            creating: false,
            creatingBusy: false,
            createForm: { name: "", priceDollars: null },
            createError: "",

            // Disable-with-reassign modal (the plan being disabled, or null).
            disableTarget: null,
            disableTargetPlanId: null,
            disableBusy: false,
            disableError: "",

            // Delete-confirm modal (the plan being deleted, or null).
            deleteTarget: null,
            deleteBusy: false,
            deleteError: "",

            // Reassign-distributor tool (single, by id).
            reassignForm: { distributor: null, planId: null },
            reassigning: false,
            reassignResult: "",
            reassignError: "",

            // Grandfathering / bulk repoint (moved from TierVersions.vue,
            // 2026-08-28). `allVersions` feeds BOTH the browse picker and
            // the repoint target picker -- they are the same list of plan
            // versions asked two different questions, so deriving them from
            // one fetch is what stops them drifting apart.
            allVersions: [],
            versionError: "",
            browseVersionId: null,
            distLoading: false,
            distributors: [],
            selectedDistributorIds: [],
            repointTargetId: null,
            repointConfirmOpen: false,
            repointing: false,
            repointResult: null,

            // ---- Supplier availability per plan -------------------------
            // `supplierTiers` is the SERVER's matrix with local edits applied
            // in place. `dirtyTiers` records which tier_keys the user
            // actually touched, so Save sends only those -- a blind save of
            // every tier would rewrite rows nobody edited and stamp
            // updated_at across the table, destroying the audit signal for
            // who changed what.
            supplierTiers: [],
            unconfiguredSuppliers: [],
            supplierLoading: true,
            // WHICH tier is mid-save / just saved, not merely THAT one is.
            // The controls are per-card now, so a page-wide boolean would
            // put "Saving..." and "Saved." on every card at once -- and a
            // card would confirm a write it did not make.
            supplierSavingTier: null,
            supplierSavedTier: null,
            supplierError: "",
            dirtyTiers: [],

            // ---- White-label branding per plan --------------------------
            // A MAP of tier_key -> boolean, not a list. Every card reads the
            // same map, so two plans sharing a tier are two views of one
            // flag and cannot disagree (design-philosophy #4) -- the same
            // reason the supplier cards read one `supplierTiers` object.
            //
            // No dirty set and no Save button: this is ONE boolean with no
            // meaningful half-edited state, so it saves on click like
            // `is_public` does. A Save button here could only ever confirm
            // the click that preceded it.
            whiteLabelByTier: {},
            whiteLabelLoading: true,
            // WHICH tier is mid-save -- a page-wide boolean would disable
            // every card's switch while one of them writes.
            whiteLabelSavingTier: null,
            // WHICH tier just saved. An instant save with no confirmation is
            // invisible -- the admin cannot tell "written" from "about to be
            // written" from "quietly failed". Per tier, not page-wide, or one
            // card would confirm a write another card made.
            whiteLabelSavedTier: null,
            whiteLabelError: "",

            // Digitizing / vectorizing charges (Trello #572): the server's
            // row per tier. Saved with an explicit Save inside the card (three
            // fields), so the draft lives in the card, not here.
            artServicesByTier: {},
            artServicesLoading: true,
            artServicesSavingTier: null,
            artServicesSavedTier: null,
            // The tier the error belongs to, or "*" for a failed page load.
            artServicesErrorTier: null,
            artServicesError: "",

            // Pending debounce timers for inline name edits, keyed by plan id.
            _nameTimers: {},
        };
    },
    watch: {
        // (Re)bind drag whenever the card list (re)appears: the container is
        // under a v-if chain, so a refetch (loading=true) unmounts it.
        showPlanCards() {
            this.$nextTick(() => this._initPlanSortable());
        },
    },
    computed: {
        showPlanCards() {
            return !this.loading && !this.errorMsg && this.plans.length > 0;
        },
        // The plan chosen in the single-reassign target select (or null).
        reassignTargetPlan() {
            return (
                this.plans.find((p) => p.id === this.reassignForm.planId) ||
                null
            );
        },

        // True when the picked distributor is already pinned to the target
        // plan, in ANY subscription state (a trial included): moving them
        // would be a no-op, so the form blocks it and says why. Compared by
        // plan id -- two plans may share a name. A row with no pinned plan
        // falls through to the server, which answers any no-op itself.
        reassignAlreadyOnTarget() {
            const d = this.reassignForm.distributor;
            const t = this.reassignTargetPlan;
            return !!(d && t && d.plan_id != null && d.plan_id === t.id);
        },

        // Valid targets for the disable-with-reassign picker: other ACTIVE,
        // non-disabled plans (a subscriber can't be moved onto the plan being
        // disabled, nor onto an already-disabled one).
        disableTargetOptions() {
            if (!this.disableTarget) return [];
            return this.plans.filter(
                (p) =>
                    p.id !== this.disableTarget.id && p.is_active !== false,
            );
        },

        // A repoint needs BOTH a selection and a destination. Guarding on
        // only one of them is how a bulk email goes out to the wrong set.
        canRepoint() {
            return (
                this.selectedDistributorIds.length > 0 &&
                this.repointTargetId != null &&
                !this.repointing
            );
        },

        repointTargetLabel() {
            const v = this.allVersions.find(
                (x) => x.id === this.repointTargetId,
            );
            return v ? `${v.tier_key} / ${v.version_label}` : "";
        },

        // plan_id -> tier_key, derived from the plan-versions list the page
        // already fetches. NOT a hardcoded frontend map: which tier a plan
        // belongs to is data a SuperAdmin can change, so reading it from a
        // constant would be authority living in code (design-philosophy #7)
        // and would go stale the first time a plan is created here -- the
        // create endpoint assigns tier 'standard' server-side.
        //
        // Many-to-one is real and intended: the route-fork migration says
        // so explicitly ("multiple plans can map to the same tier"). Two
        // plans on one tier therefore share ONE supplier row, which is why
        // every card reads the same `supplierTiers` object rather than a
        // per-card copy (design-philosophy #4).
        tierKeyByPlanId() {
            const map = {};
            for (const v of this.allVersions) {
                if (v.plan_id == null || !v.tier_key) continue;
                // A plan's versions all sit on one tier; first wins, and a
                // superseded version names the same tier as its successor.
                if (map[v.plan_id] === undefined) {
                    map[v.plan_id] = v.tier_key;
                }
            }
            return map;
        },

        unconfiguredSupplierLabel() {
            return this.unconfiguredSuppliers
                .map((s) => s.name || s.supplier_key)
                .join(", ");
        },
    },
    async mounted() {
        await Promise.all([
            this.fetchPlans(),
            this.fetchGrids(),
            this.fetchVersions(),
            this.loadSupplierMatrix(),
            this.loadWhiteLabelMatrix(),
            this.loadArtServicesMatrix(),
        ]);
    },
    beforeUnmount() {
        this._destroyPlanSortable();
    },
    methods: {
        // -----------------------------------------------------------------
        // Reorder (drag handle or move buttons) -> pricing_plan.sort_order
        // -----------------------------------------------------------------
        _destroyPlanSortable() {
            if (this._planSortable) {
                this._planSortable.destroy();
                this._planSortable = null;
            }
        },

        // Same sortablejs pattern as the store editor's Departments page
        // (StoreCategories/index.vue): handle-scoped, forceFallback so no
        // native HTML5 DnD events leak into the cards' inputs.
        _initPlanSortable() {
            this._destroyPlanSortable();
            const el = this.$refs.planCards;
            if (!el) return;
            this._planSortable = Sortable.create(el, {
                animation: 150,
                handle: "[data-testid^='sa-plan-card-drag-']",
                forceFallback: true,
                onEnd: ({ item, from, oldIndex, newIndex }) => {
                    // Sortable has already moved the DOM node. Put it back so
                    // Vue's keyed render (driven by `plans`) is the only thing
                    // that places cards -- otherwise a rollback would leave the
                    // dragged order on screen.
                    if (item && from) {
                        from.removeChild(item);
                        from.insertBefore(item, from.children[oldIndex] || null);
                    }
                    this.movePlan(oldIndex, newIndex);
                },
            });
        },

        // Optimistic: render the new order, PUT the full id list, and on
        // failure restore the previous order with an honest message.
        async movePlan(oldIndex, newIndex) {
            if (
                this.reorderSaving ||
                oldIndex === newIndex ||
                newIndex < 0 ||
                newIndex >= this.plans.length ||
                oldIndex < 0 ||
                oldIndex >= this.plans.length
            ) {
                return;
            }
            const previous = [...this.plans];
            const reordered = [...previous];
            const [moved] = reordered.splice(oldIndex, 1);
            reordered.splice(newIndex, 0, moved);
            this.plans = reordered;
            this.reorderError = "";
            this.reorderSaving = true;
            try {
                await reorderPlans(reordered.map((p) => p.id));
            } catch (e) {
                this.plans = previous;
                this.reorderError =
                    "The plan order was not saved, so the list is back in its previous order. Please try again.";
                // A 400 means our list no longer matches the server's plan
                // set (a plan was added or archived elsewhere): reload it so
                // a retry submits the current set. The error stays visible.
                if (e && e.response && e.response.status === 400) {
                    await this.fetchPlans();
                }
            } finally {
                this.reorderSaving = false;
            }
        },

        onGlobalFeeRateChange(rate) {
            this.globalPlatformFeeRate = rate;
        },

        // -----------------------------------------------------------------
        // Fetch
        // -----------------------------------------------------------------
        async fetchPlans() {
            this.loading = true;
            this.errorMsg = "";
            try {
                const resp = await listPlans();
                const data = resp && resp.data ? resp.data : resp;
                this.plans = (data && data.plans) || [];
            } catch (e) {
                this.errorMsg = "Could not load plans. Please try again.";
                this.plans = [];
            } finally {
                this.loading = false;
            }
        },

        async fetchGrids() {
            try {
                if (typeof getDecorationGrids !== "function") {
                    this.gridTiers = [];
                    return;
                }
                const resp = await getDecorationGrids();
                const data = resp && resp.data ? resp.data : resp;
                this.gridTiers = (data && data.tiers) || [];
            } catch (e) {
                this.gridTiers = [];
            }
        },

        // Match a plan to its decoration-grids entry (by plan_id, falling
        // back to plan_key). Returns null when the endpoint has no grids for
        // it (a newly-created or non-tier plan) -- the card shows a note.
        gridTierForPlan(plan) {
            return (
                this.gridTiers.find(
                    (t) =>
                        t.plan_id === plan.id ||
                        (plan.key && t.plan_key === plan.key),
                ) || null
            );
        },

        // Per-order app fee for a plan. The list endpoint does not carry the
        // fee yet, so display-only "--" until it does (kept as a seam so the
        // card can render it once the wire adds it).
        // eslint-disable-next-line no-unused-vars
        appFeeForPlan(plan) {
            return null;
        },

        // -----------------------------------------------------------------
        // Accordion
        // -----------------------------------------------------------------
        onToggleExpand(planId) {
            this.openPlanId = this.openPlanId === planId ? null : planId;
        },

        // -----------------------------------------------------------------
        // Inline edits (controlled-component contract: card emits, parent
        // persists). Name debounced; price immediate (on blur).
        // -----------------------------------------------------------------
        onUpdateName(planId, name) {
            if (this._nameTimers[planId]) {
                clearTimeout(this._nameTimers[planId]);
            }
            this._nameTimers[planId] = setTimeout(() => {
                delete this._nameTimers[planId];
                this._patchPlan(planId, { title: name });
            }, NAME_DEBOUNCE_MS);
        },

        // Free-trial length: not a price, so an immediate in-place PATCH.
        onUpdateTrialDays(planId, days) {
            this._patchPlan(planId, { trial_days: days });
        },

        // Rich-text description: not a price, so an in-place PATCH. The server
        // sanitizes; the card then shows the stored (sanitized) value.
        onUpdateDescription(planId, html) {
            return this._patchPlan(planId, { description: html });
        },

        // Card icon / accent ({ icon_key } or { accent_key }): presentation,
        // not a price, so an in-place PATCH. The server validates the key
        // against its allow-list; a refusal shows on the card.
        onUpdatePresentation(planId, payload) {
            return this._patchPlan(planId, payload);
        },

        // The monthly (flat) price: the server PUBLISHES A NEW PLAN VERSION
        // for it (a pinned subscriber keeps the old row), so the version list
        // the repoint section reads is refreshed after the save.
        async onUpdatePrice(planId, cents) {
            await this._patchPlan(planId, { monthly_price_cents: cents });
            await this.fetchVersions();
        },

        // "Per active store" / "Per inactive store". NOT the PATCH path: a
        // store-class price change publishes a NEW plan version server-side
        // (a pinned version references the charge row, so editing it in
        // place would reprice grandfathered subscribers). The version list
        // feeds the repoint section below, so it is refreshed after a save.
        async onUpdateStoreClassPrice(planId, storeClass, cents) {
            this._setPlanError(planId, "");
            try {
                const resp = await setStoreClassPrice(planId, {
                    store_class: storeClass,
                    amount_cents: cents,
                });
                const data = resp && resp.data ? resp.data : resp;
                if (data && data.plan) this._replacePlan(planId, data.plan);
                await this.fetchVersions();
            } catch (e) {
                this._setPlanError(
                    planId,
                    serverMessage(e, "Could not save the per-store price."),
                );
            }
        },

        // Included stores. Like a store-class price, NOT the PATCH path: the
        // allowance lives on the versioned per-active-store charge, so a
        // change publishes a NEW plan version. The version list feeds the
        // repoint section, so it is refreshed after a save.
        async onUpdateIncludedStores(planId, count) {
            this._setPlanError(planId, "");
            try {
                const resp = await setPlanIncludedStores(planId, count);
                const data = resp && resp.data ? resp.data : resp;
                if (data && data.plan) this._replacePlan(planId, data.plan);
                await this.fetchVersions();
            } catch (e) {
                this._setPlanError(
                    planId,
                    serverMessage(e, "Could not save the included stores."),
                );
            }
        },

        async _patchPlan(planId, payload) {
            this._setPlanError(planId, "");
            try {
                const resp = await patchPlan(planId, payload);
                const updated = resp && resp.data ? resp.data : resp;
                this._replacePlan(planId, updated);
            } catch (e) {
                this._setPlanError(
                    planId,
                    serverMessage(e, "Could not save the plan."),
                );
            }
        },

        // -----------------------------------------------------------------
        // Public / Hidden toggle -- instant PATCH.
        // -----------------------------------------------------------------
        onTogglePublic(planId, nextIsPublic) {
            this._patchPlan(planId, { is_public: nextIsPublic });
        },

        // -----------------------------------------------------------------
        // Default for new self-sign-ups -- radio semantics. The server clears
        // the previous default in the same transaction, so the whole list is
        // re-read afterwards (patching only this row would leave two cards
        // showing the pill). A refusal (private / disabled plan) leaves the
        // list alone and shows the server's message on the card.
        // -----------------------------------------------------------------
        async onSetDefaultSignup(planId) {
            this._setPlanError(planId, "");
            try {
                await patchPlan(planId, { is_default_signup: true });
                await this.fetchPlans();
            } catch (e) {
                this._setPlanError(
                    planId,
                    serverMessage(e, "Could not set the default sign-up plan."),
                );
            }
        },

        // -----------------------------------------------------------------
        // Platform-fee override -- instant PATCH.
        //
        // `rate` is a FRACTION or null; the card has already converted from
        // the percent the admin typed. null is sent EXPLICITLY (it clears
        // the override back to the global rate) and 0 is sent as 0 (a real
        // zero fee) -- the payload must not collapse them, so no truthiness
        // filtering here. SuperAdmin-only; the backend body-enforces the
        // capability and writes a pricing_audit_log row.
        // -----------------------------------------------------------------
        onUpdateFeeOverride(planId, rate) {
            this._patchPlan(planId, { platform_fee_rate_override: rate });
        },

        // ThreadFlow's shipping surcharge ({ shipping_markup_pct } or
        // { shipping_flat_handling_cents }): not a subscription price, so an
        // in-place PATCH. The server validates the range and re-checks
        // SuperAdmin; a refusal shows on the card.
        onUpdateShippingSurcharge(planId, payload) {
            return this._patchPlan(planId, payload);
        },

        // -----------------------------------------------------------------
        // Active / Disabled toggle.
        //   re-activate (nextIsActive true)  -> PATCH is_active=true.
        //   disable (nextIsActive false):
        //     zero subscribers -> POST /disable with no target.
        //     subscribers      -> open the reassign modal.
        // -----------------------------------------------------------------
        async onToggleActive(planId, nextIsActive) {
            const plan = this.plans.find((p) => p.id === planId);
            if (!plan) return;
            if (nextIsActive) {
                this._patchPlan(planId, { is_active: true });
                return;
            }
            // Disabling.
            if ((plan.subscriber_count || 0) === 0) {
                this._setPlanError(planId, "");
                try {
                    await disablePlan(planId, {});
                    await this.fetchPlans();
                } catch (e) {
                    this._setPlanError(
                        planId,
                        serverMessage(e, "Could not disable the plan."),
                    );
                }
                return;
            }
            // Has subscribers -- force a reassign target via the modal.
            this.disableTarget = plan;
            this.disableTargetPlanId = null;
            this.disableError = "";
        },

        cancelDisable() {
            this.disableTarget = null;
            this.disableTargetPlanId = null;
            this.disableError = "";
        },

        async submitDisable() {
            if (!this.disableTarget || !this.disableTargetPlanId) return;
            this.disableBusy = true;
            this.disableError = "";
            const planId = this.disableTarget.id;
            try {
                await disablePlan(planId, {
                    target_plan_id: this.disableTargetPlanId,
                });
                this.disableTarget = null;
                this.disableTargetPlanId = null;
                await this.fetchPlans();
            } catch (e) {
                this.disableError = serverMessage(
                    e,
                    "Could not disable the plan.",
                );
            } finally {
                this.disableBusy = false;
            }
        },

        // -----------------------------------------------------------------
        // Delete (archive) -- confirm dialog, then POST /archive.
        // -----------------------------------------------------------------
        onRequestDelete(planId) {
            const plan = this.plans.find((p) => p.id === planId);
            if (!plan) return;
            if ((plan.subscriber_count || 0) > 0) return; // guarded
            this.deleteTarget = plan;
            this.deleteError = "";
        },

        cancelDelete() {
            this.deleteTarget = null;
            this.deleteError = "";
        },

        async confirmDelete() {
            if (!this.deleteTarget) return;
            this.deleteBusy = true;
            this.deleteError = "";
            const planId = this.deleteTarget.id;
            try {
                await archivePlan(planId);
                this.deleteTarget = null;
                if (this.openPlanId === planId) this.openPlanId = null;
                await this.fetchPlans();
            } catch (e) {
                this.deleteError = serverMessage(
                    e,
                    "Could not delete the plan.",
                );
                // The plan changed under us (e.g. a signup landed on it):
                // re-read so the card stops offering Delete. The server's
                // message stays in the modal.
                await this.fetchPlans();
            } finally {
                this.deleteBusy = false;
            }
        },

        // -----------------------------------------------------------------
        // New plan
        // -----------------------------------------------------------------
        openCreate() {
            this.creating = true;
            this.createForm = { name: "", priceDollars: null };
            this.createError = "";
        },

        cancelCreate() {
            this.creating = false;
            this.createError = "";
        },

        async submitCreate() {
            const name = (this.createForm.name || "").trim();
            if (!name) {
                this.createError = "A display name is required.";
                return;
            }
            this.creatingBusy = true;
            this.createError = "";
            try {
                const payload = { name };
                // Blank = no flat fee. Anything typed must parse strictly and
                // be non-negative: a slip is refused, never published as a
                // partial read ("12abc" -> $12) or a negative fee.
                const rawPrice = this.createForm.priceDollars;
                const cents = dollarsToCents(rawPrice);
                const typed = String(rawPrice ?? "").trim() !== "";
                if (typed && (cents === null || cents < 0)) {
                    this.createError = "Enter a valid price, or leave it blank.";
                    return; // finally{} clears creatingBusy
                }
                if (cents !== null) payload.monthly_price_cents = cents;
                const resp = await createPlan(payload);
                const created = resp && resp.data ? resp.data : resp;
                this.creating = false;
                await this.fetchPlans();
                // Open the freshly-created card.
                if (created && created.id) this.openPlanId = created.id;
            } catch (e) {
                this.createError = serverMessage(
                    e,
                    "Could not create the plan.",
                );
            } finally {
                this.creatingBusy = false;
            }
        },

        // -----------------------------------------------------------------
        // Reassign-distributor tool (unchanged from the pre-redesign page).
        // -----------------------------------------------------------------
        async submitReassign() {
            this.reassignResult = "";
            this.reassignError = "";
            const picked = this.reassignForm.distributor;
            const distId = picked ? Number(picked.id) : 0;
            const planId = this.reassignForm.planId;
            if (!distId || !planId) {
                this.reassignError =
                    "Choose a distributor and a target plan.";
                return;
            }
            if (this.reassignAlreadyOnTarget) return;
            const distLabel = picked.name;
            // Captured before the request: the form resets on success.
            const targetName = this.reassignTargetPlan
                ? this.reassignTargetPlan.name
                : "";
            this.reassigning = true;
            try {
                const resp = await reassignDistributorPlan(distId, {
                    plan_id: planId,
                });
                const data = resp && resp.data ? resp.data : resp;
                // The plan's display NAME, never its internal key (#416).
                const planName =
                    (data && data.plan_name) || targetName || "the selected plan";
                if (data && data.status === "already_on_plan") {
                    this.reassignResult = `${distLabel} is already on ${planName}.`;
                } else {
                    this.reassignResult = `${distLabel} reassigned to ${planName}.`;
                }
                // Done: reset so the next move starts from an empty picker.
                this.reassignForm.distributor = null;
                this.reassignForm.planId = null;
            } catch (e) {
                this.reassignError = serverMessage(
                    e,
                    "Could not reassign the distributor. Please try again.",
                );
            } finally {
                this.reassigning = false;
            }
        },

        // -----------------------------------------------------------------
        // Grandfathering + bulk repoint (same product question as the single
        // reassign above -- see the section comment in the template).
        // -----------------------------------------------------------------
        formatDate(iso) {
            try {
                return new Date(iso).toLocaleString();
            } catch (e) {
                return iso;
            }
        },

        async fetchVersions() {
            this.versionError = "";
            try {
                const resp = await listPlanVersions();
                const data = resp && resp.data ? resp.data : resp;
                this.allVersions = (data && data.plan_versions) || [];
            } catch (e) {
                this.allVersions = [];
                this.versionError =
                    "Could not load plan versions. Grandfathered distributors cannot be listed or repointed until this loads.";
            }
        },

        async onBrowseVersion() {
            // A version change invalidates the selection made against the
            // PREVIOUS version's table -- carrying those ids forward would
            // repoint distributors the admin can no longer see.
            this.selectedDistributorIds = [];
            this.repointConfirmOpen = false;
            this.repointResult = null;
            if (this.browseVersionId == null) {
                this.distributors = [];
                return;
            }
            await this.loadDistributors(this.browseVersionId);
        },

        async loadDistributors(versionId) {
            this.distLoading = true;
            this.distributors = [];
            this.versionError = "";
            try {
                const resp = await distributorsOnVersion(versionId);
                const data = resp && resp.data ? resp.data : resp;
                this.distributors = (data && data.distributors) || [];
            } catch (e) {
                this.versionError =
                    "Could not load the distributors on this version.";
            } finally {
                this.distLoading = false;
            }
        },

        openRepointConfirm() {
            if (!this.canRepoint) return;
            this.repointResult = null;
            this.repointConfirmOpen = true;
        },

        async doRepoint() {
            this.repointing = true;
            this.versionError = "";
            try {
                const resp = await repointDistributors(
                    this.repointTargetId,
                    this.selectedDistributorIds,
                );
                this.repointResult = resp && resp.data ? resp.data : resp;
                this.repointConfirmOpen = false;
                this.selectedDistributorIds = [];
                // Re-read the browsed version so repointed rows leave it (or
                // gain a repointed_at). Trusting the local copy would leave
                // the admin looking at a table the backend disagrees with.
                if (this.browseVersionId != null) {
                    await this.loadDistributors(this.browseVersionId);
                }
            } catch (e) {
                this.versionError =
                    "Repoint failed. No distributors were moved.";
                this.repointConfirmOpen = false;
            } finally {
                this.repointing = false;
            }
        },

        // -----------------------------------------------------------------
        // Supplier availability per plan
        // -----------------------------------------------------------------
        async loadSupplierMatrix() {
            this.supplierLoading = true;
            this.supplierError = "";
            try {
                const resp = await getTierSuppliers();
                const data = resp && resp.data ? resp.data : resp;
                this.supplierTiers = (data && data.tiers) || [];
                this.unconfiguredSuppliers =
                    (data && data.unconfigured_suppliers) || [];
                this.dirtyTiers = [];
            } catch (e) {
                this.supplierError =
                    "Could not load supplier availability. You may not have SuperAdmin access.";
            } finally {
                this.supplierLoading = false;
            }
        },

        // Which tier's supplier row does this plan read/write? Derived from
        // data (see `tierKeyByPlanId`), null when the plan has no version --
        // the card then says so rather than showing an empty supplier list.
        tierKeyForPlan(plan) {
            return this.tierKeyByPlanId[plan.id] || null;
        },

        supplierTierForPlan(plan) {
            const tierKey = this.tierKeyForPlan(plan);
            if (!tierKey) return null;
            return (
                this.supplierTiers.find((t) => t.tier_key === tierKey) || null
            );
        },

        // Keyed by TIER, not by plan: the row being written belongs to the
        // tier, and several plans can share it. Mutating the parent's own
        // tier object is what makes two cards on one tier agree without
        // anything syncing them.
        onSupplierToggle(tierKey, supplierKey, checked) {
            const tier = this.supplierTiers.find(
                (t) => t.tier_key === tierKey,
            );
            if (!tier) return;
            const supplier = tier.suppliers.find(
                (s) => s.supplier_key === supplierKey,
            );
            if (!supplier) return;
            supplier.is_enabled = checked;
            this.supplierSavedTier = null;
            if (!this.dirtyTiers.includes(tierKey)) {
                this.dirtyTiers.push(tierKey);
            }
        },

        // ONE PUT for ONE tier -- which is exactly what the endpoint takes
        // (PUT /superadmin/pricing/tier-suppliers with a single tier_key),
        // so the per-card save needed no backend change. Saving from either
        // of two cards that share a tier writes that tier once; it does not
        // fire per plan.
        async saveTierSuppliers(tierKey) {
            if (!this.dirtyTiers.includes(tierKey)) return;
            const tier = this.supplierTiers.find(
                (t) => t.tier_key === tierKey,
            );
            if (!tier) return;

            this.supplierSavingTier = tierKey;
            this.supplierSavedTier = null;
            this.supplierError = "";
            try {
                const payload = {};
                // EVERY supplier, not only the enabled ones -- a disabled
                // row is what records that somebody decided, and it is what
                // keeps the supplier out of the "not in any plan" warning.
                for (const s of tier.suppliers) {
                    payload[s.supplier_key] = !!s.is_enabled;
                }
                await setTierSuppliers(tierKey, payload);
                // Re-read rather than trust our local copy: the response
                // carries a freshly computed warning list, and a supplier may
                // have just left or entered it.
                await this.loadSupplierMatrix();
                this.supplierSavedTier = tierKey;
            } catch (e) {
                // Re-read FIRST: `loadSupplierMatrix` clears `supplierError`
                // on entry, so setting the message before it would leave a
                // failed save with no explanation on screen. Same bug found
                // on the DDP by ddp-supplier-override.spec.js, 2026-08-27.
                await this.loadSupplierMatrix();
                this.supplierError =
                    "Could not save supplier availability. No changes were applied to the plans still listed as unsaved.";
            } finally {
                this.supplierSavingTier = null;
            }
        },

        // -----------------------------------------------------------------
        // White-label branding per plan
        //
        // Jake, 2026-09-18/19: "This should be a feature of the subscription
        // tier system, and should default to off/disabled." The GATE is
        // tier-level; the store's logo is separate (Unit 5). This page owns
        // only the gate.
        // -----------------------------------------------------------------
        async loadWhiteLabelMatrix() {
            this.whiteLabelLoading = true;
            this.whiteLabelError = "";
            try {
                const resp = await getTierWhiteLabel();
                const data = resp && resp.data ? resp.data : resp;
                const map = {};
                for (const t of (data && data.tiers) || []) {
                    if (!t || !t.tier_key) continue;
                    map[t.tier_key] = !!t.is_enabled;
                }
                this.whiteLabelByTier = map;
            } catch (e) {
                // An off switch and a failed fetch look identical otherwise,
                // and "white-labeling is disabled for this plan" is a very
                // misleading thing to state when we do not actually know
                // (design-philosophy #5).
                this.whiteLabelError =
                    "Could not load white-label branding. You may not have SuperAdmin access.";
            } finally {
                this.whiteLabelLoading = false;
            }
        },

        // A tier with no row is OFF, which is Jake's default and is why
        // enabling nothing on the day this ships changes nothing. `false`,
        // not `undefined`: the card binds it to a checkbox, and undefined
        // would leave the input uncontrolled.
        whiteLabelForPlan(plan) {
            const tierKey = this.tierKeyForPlan(plan);
            if (!tierKey) return false;
            return !!this.whiteLabelByTier[tierKey];
        },

        // Optimistic, then reconciled -- and REVERTED on failure.
        //
        // With no Save button there is no unsaved state to sit in, so a
        // failed write that leaves the switch ON is a confident lie about
        // production: it would read as "this tier is white-labeled" when it
        // is not. Reverting is not politeness, it is the only way the
        // surface can stay truthful.
        async onWhiteLabelToggle(tierKey, checked) {
            if (!tierKey) return;
            const previous = !!this.whiteLabelByTier[tierKey];
            if (previous === checked) return;

            this.whiteLabelByTier = {
                ...this.whiteLabelByTier,
                [tierKey]: checked,
            };
            this.whiteLabelSavingTier = tierKey;
            this.whiteLabelSavedTier = null;
            this.whiteLabelError = "";
            try {
                const resp = await setTierWhiteLabel(tierKey, checked);
                // Reconcile against what the SERVER says rather than trusting
                // the optimistic value: the response carries every tier, so a
                // write that landed differently than asked shows up here
                // instead of silently persisting on screen.
                const data = resp && resp.data ? resp.data : resp;
                const tiers = (data && data.tiers) || null;
                if (tiers) {
                    const map = {};
                    for (const t of tiers) {
                        if (!t || !t.tier_key) continue;
                        map[t.tier_key] = !!t.is_enabled;
                    }
                    this.whiteLabelByTier = map;
                }
                this.whiteLabelSavedTier = tierKey;
            } catch (e) {
                this.whiteLabelByTier = {
                    ...this.whiteLabelByTier,
                    [tierKey]: previous,
                };
                // A switch that springs back with no message looks like a
                // broken control, and the admin's next move is to click it
                // again.
                this.whiteLabelError =
                    "Could not save white-label branding. The plan is unchanged.";
            } finally {
                this.whiteLabelSavingTier = null;
            }
        },

        // -----------------------------------------------------------------
        // Digitizing / vectorizing charges (Trello #572)
        //
        // Jake, 2026-10-27/28: a setting on the subscription tier with two
        // modes, charge immediately (the default) or add to the monthly
        // subscription invoice. Every tier comes back from the server,
        // unconfigured ones at the decided defaults.
        // -----------------------------------------------------------------
        _artServicesMap(data) {
            const map = {};
            for (const t of (data && data.tiers) || []) {
                if (!t || !t.tier_key) continue;
                map[t.tier_key] = {
                    billing_mode: t.billing_mode,
                    digitizing_cents: t.digitizing_cents,
                    vectorizing_cents: t.vectorizing_cents,
                    is_configured: !!t.is_configured,
                };
            }
            return map;
        },

        async loadArtServicesMatrix() {
            this.artServicesLoading = true;
            this.artServicesError = "";
            this.artServicesErrorTier = null;
            try {
                const resp = await getTierArtServices();
                this.artServicesByTier = this._artServicesMap(
                    resp && resp.data ? resp.data : resp,
                );
            } catch (e) {
                // Never show the defaults as if they were the tier's setting
                // when we do not know what it is (design-philosophy #5).
                this.artServicesErrorTier = "*";
                this.artServicesError =
                    "Could not load digitizing and vectorizing charges.";
            } finally {
                this.artServicesLoading = false;
            }
        },

        artServicesForPlan(plan) {
            const tierKey = this.tierKeyForPlan(plan);
            if (!tierKey) return null;
            return this.artServicesByTier[tierKey] || null;
        },

        // Not optimistic: the card keeps the admin's draft until the server
        // answers, and a failure leaves that draft in place for a retry.
        async onSaveArtServices(tierKey, setting) {
            if (!tierKey || this.artServicesSavingTier) return;
            this.artServicesSavingTier = tierKey;
            this.artServicesSavedTier = null;
            this.artServicesError = "";
            this.artServicesErrorTier = null;
            try {
                const resp = await setTierArtServices(setting);
                this.artServicesByTier = this._artServicesMap(
                    resp && resp.data ? resp.data : resp,
                );
                this.artServicesSavedTier = tierKey;
            } catch (e) {
                const msg =
                    e && e.response && e.response.data && e.response.data.message;
                this.artServicesErrorTier = tierKey;
                this.artServicesError = msg
                    ? `Could not save: ${msg}`
                    : "Could not save digitizing and vectorizing charges. The tier is unchanged.";
            } finally {
                this.artServicesSavingTier = null;
            }
        },

        // -----------------------------------------------------------------
        // Helpers
        // -----------------------------------------------------------------
        _replacePlan(planId, updated) {
            if (!updated || typeof updated !== "object") return;
            const idx = this.plans.findIndex((p) => p.id === planId);
            if (idx !== -1) this.plans.splice(idx, 1, updated);
        },

        _setPlanError(planId, message) {
            // Reassign a fresh object so Vue reactivity picks up the change.
            this.planSaveErrors = { ...this.planSaveErrors, [planId]: message };
        },
    },
};
</script>

<style scoped>
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 16px;
    color: #1a202c;
}
/*
  Platform-wide settings as a 2x2 grid (Jake feedback 2026-08-04). Two
  equal columns; each child settings <section> becomes one cell. Collapses
  to a single column below 900px. The child sections carry their own
  margin-bottom (24px) for the stacked layout; inside the grid we zero it
  and let `gap` own the spacing so the 2x2 stays even.
*/
.sa-settings-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    align-items: start;
    margin-bottom: 28px;
}
.sa-settings-grid > :deep(section) {
    margin-bottom: 0;
    height: 100%;
}
@media (max-width: 900px) {
    .sa-settings-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}
.sa-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-state--error {
    color: #c53030;
}
.sa-plans {
    margin-top: 28px;
}
.sa-plans__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}
.sa-btn {
    border-radius: 4px;
    padding: 6px 14px;
    font-size: 13px;
    cursor: pointer;
    border: 1px solid #cbd5e0;
    background: #edf2f7;
    color: #2d3748;
}
.sa-btn--primary {
    background: #3182ce;
    border-color: #3182ce;
    color: #fff;
}
.sa-btn--ghost {
    background: transparent;
    border-color: #cbd5e0;
    color: #4a5568;
}
.sa-btn--danger {
    background: #c53030;
    border-color: #c53030;
    color: #fff;
}

/* Modal */
.sa-modal__notice {
    font-size: 12px;
    color: #744210;
    background: #fefcbf;
    border: 1px solid #f6e05e;
    border-radius: 4px;
    padding: 8px 10px;
    margin: 0;
    line-height: 1.5;
}
.sa-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.sa-modal {
    background: #fff;
    border-radius: 8px;
    width: 420px;
    max-width: 95vw;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.18);
    padding: 24px;
}
.sa-modal__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
}
.sa-modal__title {
    font-size: 17px;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
}
.sa-modal__close {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: var(--tf-fg-3);
    padding: 0 4px;
}
.sa-modal__form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.sa-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.sa-label {
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.sa-input {
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    padding: 6px 10px;
    font-size: 14px;
    color: #1a202c;
    background: #fff;
}
.sa-input-dollar {
    display: flex;
    align-items: center;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    background: #fff;
    overflow: hidden;
}
.sa-input-dollar__prefix {
    padding: 6px 8px 6px 10px;
    font-size: 14px;
    color: #4a5568;
    background: #f7fafc;
    border-right: 1px solid #e2e8f0;
    user-select: none;
}
.sa-input--dollar {
    border: none;
    border-radius: 0;
    flex: 1;
    padding: 6px 10px;
}
.sa-save-error {
    font-size: 13px;
    color: #c53030;
    margin: 0;
}
.sa-modal__actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    margin-top: 4px;
}

/* Reassign section */
.sa-section-title {
    font-size: 17px;
    font-weight: 600;
    color: #1a202c;
    margin: 28px 0 4px;
}
.sa-section-hint {
    font-size: 13px;
    color: #4a5568;
    margin: 0 0 12px;
    line-height: 1.5;
}
.sa-reassign__form {
    display: flex;
    gap: 14px;
    align-items: flex-end;
    flex-wrap: wrap;
}
.sa-reassign__form .sa-field {
    min-width: 180px;
}
.sa-reassign__note {
    font-size: 13px;
    color: #744210;
    margin: 10px 0 0;
}
.sa-reassign__result {
    font-size: 13px;
    color: #276749;
    margin: 10px 0 0;
}

/* --- Supplier availability matrix + grandfathering table ------------- */
.sa-subsection-title {
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
    margin: 20px 0 4px;
}
.sa-warning {
    color: #744210;
    background: #fffff0;
    border: 1px solid #f6e05e;
    border-radius: 4px;
    padding: 8px 12px;
    margin: 0 0 12px;
    max-width: 720px;
    font-size: 13px;
}
.sa-matrix {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 8px;
}
.sa-matrix th {
    text-align: left;
    background: #f7fafc;
    color: #2d3748;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 10px 12px;
    border-bottom: 1px solid #e2e8f0;
}
.sa-matrix td {
    padding: 10px 12px;
    border-bottom: 1px solid #edf2f7;
    font-size: 14px;
    color: #1a202c;
    vertical-align: top;
}
.sa-checkbox-col {
    width: 32px;
}
.sa-muted {
    color: var(--tf-fg-4);
    font-size: 12px;
}
.sa-repoint-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    margin: 12px 0;
    flex-wrap: wrap;
}
.sa-confirm {
    background: #fffaf0;
    border: 1px solid #f6ad55;
    border-radius: 6px;
    padding: 14px;
    margin-top: 12px;
    max-width: 720px;
    font-size: 14px;
}
</style>
